import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import authStorage from '../../utilities/Storage';

const PersonalDetailsForm = () => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [formValid, setFormValid] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [currentSection, setCurrentSection] = useState('personal');
    const [companyDetails, setCompanyDetails] = useState(null);
    const [companyLoading, setCompanyLoading] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        fullName: '',
        position: '',
        organization: '',
        phone: '',
        country: '',
        participationType: '',
        exhibitBooth: 'none',
        b2bInterest: [],
        b2bDetails: '',
        companyProfile: '',
        companySector: '',
        productsServices: '',
        companyEmail: '',
        companyAddress: '',
        companyPhone: '',
        companyWebsite: '',
        socialMedia: '',
        sponsorshipInterest: 'none',
        needInvoice: false,
        comments: 'none',
        logoUrl: ''
    });

    // Options for dropdowns
    const participationOptions = [
        "East African Business and Investment Summit & Expo2026",
        "EABC-AfDB Webinar on Market Intelligence",
    ];

    const boothOptions = [
        "EABC Members and Partners booth 3mx3m USD 300",
        "Standard Booth USD 500",
        "Premium Booth USD 800"
    ];

    const b2bInterestOptions = [
        "Supplier",
        "Investor",
        "Buyer",
        "Service Provider"
    ];

    const sponsorshipOptions = [
        "Platinum Sponsor ($30,000)",
        "Gold Sponsor ($20,000)",
        "Silver Sponsor ($10,000)",
        "Bronze Sponsor ($5,000)",
        "No"
    ];

    const titleOptions = [
        "Mr.", "Ms.", "Mrs.", "Dr.", "Prof.", "Hon.", "Amb.", "Eng."
    ];

    const sectorOptions = [
        "Agriculture",
        "Health",
        "Technology",
        "Manufacturing",
        "Finance",
        "Education",
        "Energy",
        "Transport",
        "Tourism",
        "Other"
    ];

    const loadRegistrationDetails = async (userEmail, userCompany) => {
        try {
            const response = await axios.get('https://backend-livestream-production.up.railway.app/api/get-summit-registration', {
                params: {
                    email: userEmail,
                    company_name: userCompany
                }
            });
            if (response.data) {
                setFormData(prev => ({
                    ...prev,
                    ...response.data
                }));
                if (response.data.logoUrl && response.data.logoUrl !== "no_img") {
                    setLogoPreview(`https://backend-livestream-production.up.railway.app/public${response.data.logoUrl}`);
                }
            }
        } catch (error) {
            console.error('Error loading registration details:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCompanyDetails = async (companyName) => {
        if (!companyName) {
            setCompanyDetails(null);
            return;
        }
        setCompanyLoading(true);
        try {
            const response = await axios.get('https://backend-livestream-production.up.railway.app/api/get-campany-by-name', {
                params: { company_name: companyName }
            });
            if (response.data && response.data.data && response.data.data.length > 0) {
                setCompanyDetails(response.data.data[0]);
                // Auto-fill company details if they're not already filled
                setFormData(prev => ({
                    ...prev,
                    companyProfile: prev.companyProfile || response.data.data[0].companyProfile || '',
                    companySector: prev.companySector || response.data.data[0].companySector || '',
                    productsServices: prev.productsServices || response.data.data[0].productsServices || '',
                    companyEmail: prev.companyEmail || response.data.data[0].companyEmail || '',
                    companyAddress: prev.companyAddress || response.data.data[0].companyAddress || '',
                    companyPhone: prev.companyPhone || response.data.data[0].companyPhone || '',
                    companyWebsite: prev.companyWebsite || response.data.data[0].companyWebsite || '',
                    socialMedia: prev.socialMedia || response.data.data[0].socialMedia || '',
                    logoUrl: prev.logoUrl || response.data.data[0].logoUrl || ''
                }));
                if (response.data.data[0].logoUrl && response.data.data[0].logoUrl !== "no_img" && !logoPreview) {
                    setLogoPreview(`https://backend-livestream-production.up.railway.app/public${response.data.data[0].logoUrl}`);
                }
            } else {
                // If company not found, allow user to add their own details
                setCompanyDetails(null);
                // Clear the company fields for user input
                setFormData(prev => ({
                    ...prev,
                    companyProfile: '',
                    companySector: '',
                    productsServices: '',
                    companyEmail: '',
                    companyAddress: '',
                    companyPhone: '',
                    companyWebsite: '',
                    socialMedia: '',
                    logoUrl: ''
                }));
                setLogoPreview(null);
            }
        } catch (error) {
            setCompanyDetails(null);
            setLogoPreview(null);
            console.error('Error loading company details:', error);
        } finally {
            setCompanyLoading(false);
        }
    };

    function getUserRegistrationDetails() {
        const userData = authStorage.getUser();
        if (!userData) return;
        try {
            const user = JSON.parse(userData);
            loadRegistrationDetails(user.email, user.company_name);
        } catch (error) {
            setLoading(false);
        }
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleMultiSelectChange = (e) => {
        const { options } = e.target;
        const selectedOptions = [];
        for (let i = 0; i < options.length; i++) {
            if (options[i].selected) {
                selectedOptions.push(options[i].value);
            }
        }
        setFormData(prev => ({
            ...prev,
            [e.target.name]: selectedOptions
        }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
                setFormData(prev => ({
                    ...prev,
                    logoUrl: reader.result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.fullName) newErrors.fullName = 'Full name is required';
        if (!formData.position) newErrors.position = 'Position is required';
        if (!formData.organization) newErrors.organization = 'Organization is required';
        if (!formData.phone) newErrors.phone = 'Phone number is required';
        if (!formData.country) newErrors.country = 'Country is required';
        if (!formData.participationType) newErrors.participationType = 'Events type is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);
        try {
            const userData = authStorage.getUser();
            if (!userData) throw new Error('User not authenticated');
            const user = JSON.parse(userData);

            // Create FormData for file upload
            const formDataToSend = new FormData();
            
            // Add all form fields
            formDataToSend.append('title', formData.title);
            formDataToSend.append('fullName', formData.fullName);
            formDataToSend.append('position', formData.position);
            formDataToSend.append('organization', formData.organization);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('country', formData.country);
            formDataToSend.append('participationType', formData.participationType);
            formDataToSend.append('exhibitBooth', formData.exhibitBooth);
            formDataToSend.append('b2bInterest', JSON.stringify(formData.b2bInterest));
            formDataToSend.append('b2bDetails', formData.b2bDetails);
            formDataToSend.append('companyProfile', formData.companyProfile);
            formDataToSend.append('companySector', formData.companySector);
            formDataToSend.append('productsServices', formData.productsServices);
            formDataToSend.append('companyEmail', formData.companyEmail);
            formDataToSend.append('companyAddress', formData.companyAddress);
            formDataToSend.append('companyPhone', formData.companyPhone);
            formDataToSend.append('companyWebsite', formData.companyWebsite);
            formDataToSend.append('socialMedia', formData.socialMedia);
            formDataToSend.append('sponsorshipInterest', formData.sponsorshipInterest);
            formDataToSend.append('needInvoice', formData.needInvoice);
            formDataToSend.append('comments', formData.comments);
            formDataToSend.append('email', user.email);
            formDataToSend.append('company_name', user.company_name);
            
            // Add logo file if exists
            if (logoFile) {
                formDataToSend.append('logo', logoFile);
            }

            const response = await axios.post(
                'https://backend-livestream-production.up.railway.app/api/add-personal-detail',
                formDataToSend,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            setSuccessMessage('Registration details saved successfully!');
            
            // Update user status
            try {
                const updateResponse = await axios.patch('https://backend-livestream-production.up.railway.app/api/update-user', {
                    email: user.email,
                    company_name: user.company_name
                }, {
                    params: {
                        email: user.email,
                        company_name: user.company_name
                    }
                });
                if (updateResponse.data.ok === 1) {
                    setSuccessMessage('Profile completed successfully! Redirecting...');
                    setTimeout(() => {
                        navigate('/');
                    }, 2000);
                } else {
                    throw new Error(updateResponse.data.error || 'Failed to update user status');
                }
            } catch (error) {
                setErrors({
                    submit: error.response?.data?.message ||
                        error.response?.data?.error ||
                        error.message ||
                        'Failed to complete profile. Please try again.'
                });
            }
        } catch (error) {
            setErrors({ submit: error.response?.data?.message || 'Failed to save registration details. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        getUserRegistrationDetails();
    }, []);

    useEffect(() => {
        const isValid = formData.fullName && formData.position &&
            formData.organization && formData.phone &&
            formData.country && formData.participationType;
        setFormValid(isValid);
    }, [formData]);

    // Load company details when company tab is accessed
    useEffect(() => {
        if (currentSection === 'company') {
            const userData = authStorage.getUser();
            if (userData) {
                try {
                    const user = JSON.parse(userData);
                    loadCompanyDetails(user.company_name);
                } catch (error) {
                    // Ignore parse error
                }
            }
        }
        // eslint-disable-next-line
    }, [currentSection]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="summit-registration" style={{
            background: 'linear-gradient(rgba(255,255,255,0.95), rgba(255,255,255,0.95)), url(https://images.unsplash.com/photo-1431540015161-0bf868a2d407?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '100vh',
            padding: '3rem 0'
        }}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <div className="card border-0 shadow-lg" style={{
                            borderRadius: '15px',
                            overflow: 'hidden'
                        }}>
                            {/* Conference Header */}
                            <div className="conference-header text-white text-center py-4" style={{
                                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'
                            }}>
                                <h1 className="mb-2" style={{ fontWeight: '700' }}>B2B Network</h1>
                            </div>

                            {/* Progress Steps */}
                            <div className="progress-steps bg-light py-3 px-4">
                                <div className="d-flex justify-content-between align-items-center">
                                    {['personal', 'events', 'business', 'company'].map((step) => (
                                        <button
                                            key={step}
                                            className={`step-btn ${currentSection === step ? 'active' : ''}`}
                                            onClick={() => setCurrentSection(step)}
                                        >
                                            <span className="step-number">
                                                {['personal', 'events', 'business', 'company'].indexOf(step) + 1}
                                            </span>
                                            <span className="step-label d-none d-md-inline">
                                                {step.charAt(0).toUpperCase() + step.slice(1)}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="card-body p-4 p-md-5">
                                {successMessage && (
                                    <div className="alert alert-success mb-4">
                                        <i className="bi bi-check-circle-fill me-2"></i>
                                        {successMessage}
                                    </div>
                                )}
                                {errors.submit && (
                                    <div className="alert alert-danger mb-4">
                                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                        {errors.submit}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} noValidate>
                                    {/* Personal Information Section */}
                                    <div className={`registration-section ${currentSection === 'personal' ? 'active' : 'd-none'}`}>
                                        <h4 className="section-title mb-4">
                                            <i className="bi bi-person-circle me-2"></i>
                                            Personal Information
                                        </h4>
                                        <div className="row g-3">
                                            <div className="col-md-3">
                                                <label className="form-label">Title</label>
                                                <select
                                                    className="form-select"
                                                    name="title"
                                                    value={formData.title}
                                                    onChange={handleChange}
                                                >
                                                    <option value="">Select Title</option>
                                                    {titleOptions.map((title, index) => (
                                                        <option key={index} value={title}>{title}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-9">
                                                <label className="form-label">Full Names <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.fullName ? 'is-invalid' : ''}`}
                                                    name="fullName"
                                                    value={formData.fullName}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                {errors.fullName && <div className="invalid-feedback">{errors.fullName}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Position <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.position ? 'is-invalid' : ''}`}
                                                    name="position"
                                                    value={formData.position}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                {errors.position && <div className="invalid-feedback">{errors.position}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Organization / Company <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.organization ? 'is-invalid' : ''}`}
                                                    name="organization"
                                                    value={formData.organization}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                {errors.organization && <div className="invalid-feedback">{errors.organization}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Phone (with country code) <span className="text-danger">*</span></label>
                                                <input
                                                    type="tel"
                                                    className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    placeholder="+255777777777"
                                                    required
                                                />
                                                {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Country <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.country ? 'is-invalid' : ''}`}
                                                    name="country"
                                                    value={formData.country}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                {errors.country && <div className="invalid-feedback">{errors.country}</div>}
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-between mt-4">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => navigate(-1)}
                                            >
                                                <i className="bi bi-arrow-left me-2"></i>Back
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={() => setCurrentSection('events')}
                                            >
                                                Next: events Details <i className="bi bi-arrow-right ms-2"></i>
                                            </button>
                                        </div>
                                    </div>
                                    {/* events Section */}
                                    <div className={`registration-section ${currentSection === 'events' ? 'active' : 'd-none'}`}>
                                        <h4 className="section-title mb-4">
                                            <i className="bi bi-ticket-perforated me-2"></i>
                                            Summit events
                                        </h4>
                                        <div className="row g-3">
                                            <div className="col-md-12">
                                                <label className="form-label">Event Patricpation <span className="text-danger">*</span></label>
                                                <select
                                                    className={`form-select ${errors.participationType ? 'is-invalid' : ''}`}
                                                    name="participationType"
                                                    value={formData.participationType}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    <option value="">Select Event Patricpation</option>
                                                    {participationOptions.map((option, index) => (
                                                        <option key={index} value={option}>{option}</option>
                                                    ))}
                                                </select>
                                                {errors.participationType && <div className="invalid-feedback">{errors.participationType}</div>}
                                            </div>
                                            <div className="col-md-12" style={{display:'none'}}>
                                                <label className="form-label">Exhibition Booth</label>
                                                <select
                                                    className="form-select"
                                                    name="exhibitBooth"
                                                    value={formData.exhibitBooth}
                                                    onChange={handleChange}
                                                >
                                                    <option value="none">Select Booth Option</option>
                                                    {boothOptions.map((option, index) => (
                                                        <option key={index} value={option}>{option}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-between mt-4">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => setCurrentSection('personal')}
                                            >
                                                <i className="bi bi-arrow-left me-2"></i>Previous
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={() => setCurrentSection('business')}
                                            >
                                                Next: Business Interests <i className="bi bi-arrow-right ms-2"></i>
                                            </button>
                                        </div>
                                    </div>
                                    {/* Business Interests Section */}
                                    <div className={`registration-section ${currentSection === 'business' ? 'active' : 'd-none'}`}>
                                        <h4 className="section-title mb-4">
                                            <i className="bi bi-briefcase me-2"></i>
                                            Business Interests
                                        </h4>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label">Areas of Interest in B2B Meetings</label>
                                                {b2bInterestOptions.map((option, index) => (
                                                    <div key={index} className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`b2b-${index}`}
                                                        value={option}
                                                        checked={formData.b2bInterest.includes(option)}
                                                        onChange={(e) => {
                                                        const value = e.target.value;
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            b2bInterest: prev.b2bInterest.includes(value)
                                                            ? prev.b2bInterest.filter((item) => item !== value)
                                                            : [...prev.b2bInterest, value],
                                                        }));
                                                        }}
                                                    />
                                                    <label className="form-check-label" htmlFor={`b2b-${index}`}>
                                                        {option}
                                                    </label>
                                                    </div>
                                                ))}
                                                <small className="text-muted">Hold Ctrl/Cmd to select multiple options</small>
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">Details of B2B/B2G Interest</label>
                                                <textarea
                                                    className="form-control"
                                                    name="b2bDetails"
                                                    rows="3"
                                                    value={formData.b2bDetails}
                                                    onChange={handleChange}
                                                ></textarea>
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-between mt-4">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => setCurrentSection('events')}
                                            >
                                                <i className="bi bi-arrow-left me-2"></i>Previous
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={() => setCurrentSection('company')}
                                            >
                                                Next: Company Information <i className="bi bi-arrow-right ms-2"></i>
                                            </button>
                                        </div>
                                    </div>
                                    {/* Company Information Section */}
                                    <div className={`registration-section ${currentSection === 'company' ? 'active' : 'd-none'}`}>
                                        <h4 className="section-title mb-4">
                                            <i className="bi bi-building me-2"></i>
                                            Company Information
                                        </h4>
                                        {companyLoading ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading company details...</span>
                                                </div>
                                                <p className="mt-3">Loading company details...</p>
                                            </div>
                                        ) : (
                                            <>
                                                {companyDetails && (
                                                    <div className="alert alert-info mb-4">
                                                        <i className="bi bi-info-circle me-2"></i>
                                                        Company details loaded from our records. Please review and update if needed.
                                                    </div>
                                                )}
                                                {!companyDetails && (
                                                    <div className="alert alert-warning mb-4">
                                                        <i className="bi bi-exclamation-circle me-2"></i>
                                                        No company record found. Please add your company details below.
                                                    </div>
                                                )}
                                                <div className="row g-3">
                                                    <div className="col-12">
                                                        <div className="mb-3">
                                                            <label className="form-label">Company Logo</label>
                                                            <div className="d-flex align-items-center">
                                                                <div className="me-3">
                                                                    {logoPreview ? (
                                                                        <img
                                                                            src={logoPreview}
                                                                            alt="Company Logo"
                                                                            className="img-thumbnail"
                                                                            style={{ width: '100px', height: '100px', objectFit: 'contain' }}
                                                                        />
                                                                    ) : (
                                                                        <div className="bg-light d-flex align-items-center justify-content-center"
                                                                            style={{ width: '100px', height: '100px', border: '1px dashed #ccc' }}>
                                                                            <span className="text-muted">No Logo</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <input
                                                                        type="file"
                                                                        className="form-control"
                                                                        accept="image/*"
                                                                        onChange={handleLogoChange}
                                                                        style={{ maxWidth: '300px' }}
                                                                    />
                                                                    <small className="text-muted">Recommended size: 300x300px</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-12">
                                                        <label className="form-label">Company Short Profile / Description</label>
                                                        <textarea
                                                            className="form-control"
                                                            name="companyProfile"
                                                            rows="3"
                                                            value={formData.companyProfile}
                                                            onChange={handleChange}
                                                        ></textarea>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Company Sector</label>
                                                        <select
                                                            className="form-select"
                                                            name="companySector"
                                                            value={formData.companySector}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="">Select Sector</option>
                                                            {sectorOptions.map((sector, index) => (
                                                                <option key={index} value={sector}>{sector}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Products/Services Offered</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="productsServices"
                                                            value={formData.productsServices}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Company Email</label>
                                                        <input
                                                            type="email"
                                                            className="form-control"
                                                            name="companyEmail"
                                                            value={formData.companyEmail}
                                                            onChange={handleChange}
                                                            required={false}
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Company Physical Address</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="companyAddress"
                                                            value={formData.companyAddress}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Company Telephone Number</label>
                                                        <input
                                                            type="tel"
                                                            className="form-control"
                                                            name="companyPhone"
                                                            value={formData.companyPhone}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label">Company Website</label>
                                                        <input
                                                            type="url"
                                                            className="form-control"
                                                            name="companyWebsite"
                                                            value={formData.companyWebsite}
                                                            onChange={handleChange}
                                                            placeholder="https://"
                                                        />
                                                    </div>
                                                    <div className="col-12">
                                                        <label className="form-label">Social Media Links</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="socialMedia"
                                                            value={formData.socialMedia}
                                                            onChange={handleChange}
                                                            placeholder="Facebook, Twitter, LinkedIn URLs"
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        <div className="d-flex justify-content-between mt-4">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => setCurrentSection('business')}
                                            >
                                                <i className="bi bi-arrow-left me-2"></i>Previous
                                            </button>
                                           <button
                                                type="submit"
                                                className="btn btn-success"
                                                disabled={isSubmitting || !formValid}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Submitting...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-check-circle me-2"></i>
                                                        Complete Registration
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <style jsx>{`
                .summit-registration {
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                }
                .conference-header {
                    border-bottom: 3px solid #f8f9fa;
                }
                .section-title {
                    color: #2a5298;
                    font-weight: 600;
                    border-bottom: 2px solid #f0f0f0;
                    padding-bottom: 0.5rem;
                }
                .progress-steps {
                    border-bottom: 1px solid #dee2e6;
                }
                .step-btn {
                    background: none;
                    border: none;
                    position: relative;
                    padding: 0.5rem 1rem;
                    color: #6c757d;
                    font-weight: 500;
                    transition: all 0.3s;
                }
                .step-btn.active {
                    color: #2a5298;
                    font-weight: 600;
                }
                .step-number {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 30px;
                    height: 30px;
                    border-radius: 50%;
                    background: #e9ecef;
                    color: #6c757d;
                    margin-right: 0.5rem;
                }
                .step-btn.active .step-number {
                    background: #2a5298;
                    color: white;
                }
                .registration-section {
                    transition: all 0.3s ease;
                }
                .form-control, .form-select {
                    border-radius: 8px;
                    padding: 10px 15px;
                    border: 2px solid #e0e0e0;
                }
                .form-control:focus, .form-select:focus {
                    border-color: #2a5298;
                    box-shadow: 0 0 0 0.25rem rgba(42, 82, 152, 0.25);
                }
                .btn-primary {
                    background-color: #2a5298;
                    border-color: #2a5298;
                    padding: 10px 20px;
                    border-radius: 8px;
                    font-weight: 500;
                }
                .btn-primary:hover {
                    background-color: #1e3c72;
                    border-color: #1e3c72;
                }
                .btn-success {
                    padding: 10px 20px;
                    border-radius: 8px;
                    font-weight: 500;
                }
                .invalid-feedback {
                    font-size: 0.85rem;
                }
                @media (max-width: 768px) {
                    .step-label {
                        display: none;
                    }
                    .step-btn {
                        padding: 0.5rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default PersonalDetailsForm;